import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as logApi from '../../api/log';
import { message } from 'antd';

// 获取操作日志列表
export const fetchOperationLogs = createAsyncThunk(
  'log/fetchOperationLogs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await logApi.getOperationLogs(params);
      if (!response || !response.data) {
        return rejectWithValue('获取操作日志列表响应数据格式错误');
      }
      // 返回数据和查询参数，以便在reducer中保存
      return {
        data: response.data,
        params
      };
    } catch (error) {
      return rejectWithValue(error.message || '获取操作日志列表失败');
    }
  }
);

// 获取操作日志详情
export const fetchOperationLogDetail = createAsyncThunk(
  'log/fetchOperationLogDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await logApi.getOperationLogDetail(id);
      if (!response || !response.data) {
        return rejectWithValue('获取操作日志详情响应数据格式错误');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || '获取操作日志详情失败');
    }
  }
);

// 导出操作日志
export const exportOperationLogs = createAsyncThunk(
  'log/exportOperationLogs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await logApi.exportOperationLogs(params);
      
      // 处理文件下载
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // 尝试从响应头获取文件名
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'operation-logs.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('导出成功');
      return true;
    } catch (error) {
      message.error('导出失败: ' + (error.message || '未知错误'));
      return rejectWithValue(error.message || '导出操作日志失败');
    }
  }
);

// 初始状态
const initialState = {
  operationLogs: {
    data: [],
    total: 0,
    pageNum: 1,
    pageSize: 10,
    loading: false,
    error: null,
    searchParams: {}
  },
  logDetail: {
    data: null,
    loading: false,
    error: null
  },
  exportStatus: {
    loading: false,
    success: false,
    error: null
  }
};

// 创建slice
const logSlice = createSlice({
  name: 'log',
  initialState,
  reducers: {
    clearLogDetail: (state) => {
      state.logDetail = {
        data: null,
        loading: false,
        error: null
      };
    },
    clearExportStatus: (state) => {
      state.exportStatus = {
        loading: false,
        success: false,
        error: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理获取操作日志列表
      .addCase(fetchOperationLogs.pending, (state) => {
        state.operationLogs.loading = true;
        state.operationLogs.error = null;
      })
      .addCase(fetchOperationLogs.fulfilled, (state, action) => {
        state.operationLogs.loading = false;
        state.operationLogs.data = action.payload.data.records || [];
        state.operationLogs.total = action.payload.data.total || 0;
        // 保存当前分页和搜索参数信息
        if (action.payload.params) {
          state.operationLogs.pageNum = action.payload.params.pageNum || 1;
          state.operationLogs.pageSize = action.payload.params.pageSize || 10;
          state.operationLogs.searchParams = action.payload.params;
        }
      })
      .addCase(fetchOperationLogs.rejected, (state, action) => {
        state.operationLogs.loading = false;
        state.operationLogs.error = action.payload;
      })

      // 处理获取操作日志详情
      .addCase(fetchOperationLogDetail.pending, (state) => {
        state.logDetail.loading = true;
        state.logDetail.error = null;
      })
      .addCase(fetchOperationLogDetail.fulfilled, (state, action) => {
        state.logDetail.loading = false;
        state.logDetail.data = action.payload;
      })
      .addCase(fetchOperationLogDetail.rejected, (state, action) => {
        state.logDetail.loading = false;
        state.logDetail.error = action.payload;
      })

      // 处理导出操作日志
      .addCase(exportOperationLogs.pending, (state) => {
        state.exportStatus.loading = true;
        state.exportStatus.success = false;
        state.exportStatus.error = null;
      })
      .addCase(exportOperationLogs.fulfilled, (state) => {
        state.exportStatus.loading = false;
        state.exportStatus.success = true;
      })
      .addCase(exportOperationLogs.rejected, (state, action) => {
        state.exportStatus.loading = false;
        state.exportStatus.error = action.payload;
      });
  },
});

export const { clearLogDetail, clearExportStatus } = logSlice.actions;

export default logSlice.reducer; 